import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { api } from '../../api/client';

export const PASSKEY_REVOKE_ALL_BLAST_RADIUS =
  'all-passkeys-and-mobile-authorizations-for-current-account' as const;

export type PasskeyAuthenticationResponse =
  | AuthenticationResponseJSON
  | Record<string, unknown>;

export type PasskeyRegistrationResponse =
  | RegistrationResponseJSON
  | Record<string, unknown>;

export type PasskeyRegistrationOptionsInput = {
  nomeDispositivo?: string;
  isMobile?: boolean;
};

export type PasskeyRegistrationOptionsResponse =
  | PublicKeyCredentialCreationOptionsJSON
  | {
      options: PublicKeyCredentialCreationOptionsJSON;
      nomeDispositivo?: string;
      [key: string]: unknown;
    };

export type PasskeyRegistrationVerifyOptions = {
  nomeDispositivo?: string;
  navegador?: string;
  sistemaOp?: string;
  fingerprint?: string;
};

export type PasskeyApiRequestOptions = {
  bearerToken?: string;
};

export type PasskeyAuthenticationOptionsResponse =
  | PublicKeyCredentialRequestOptionsJSON
  | {
      options: PublicKeyCredentialRequestOptionsJSON;
      [key: string]: unknown;
    };

export type PasskeyStatusResponse = {
  temPasskey?: boolean;
  temPasskeyDesktop?: boolean;
  temPasskeyMobile?: boolean;
  [key: string]: unknown;
};

export type PasskeyVerifyResponse = {
  ok?: boolean;
  tipoDispositivo?: 'desktop' | 'mobile' | string;
  biometriaAtiva?: boolean;
  trustLevel?: number;
  [key: string]: unknown;
};

export type LoginPasskeyVerifyResponse = {
  access_token?: string;
  refresh_token?: string;
  user?: unknown;
  revokedCredentialId?: string | null;
  remainingCredentialIds?: Array<string | null>;
  [key: string]: unknown;
};

export type PublicPasskeyConfig = {
  ativo?: boolean;
  enabled?: boolean;
  [key: string]: unknown;
};

export type RevokeAllPasskeysWithExplicitConsentInput = {
  explicitConsent: true;
  acknowledgedBlastRadius: typeof PASSKEY_REVOKE_ALL_BLAST_RADIUS;
  reason?: string;
};

export type PasskeyScopedRevokeInput = {
  explicitConsent: true;
  reason?: string;
  fcmToken?: string;
};

export type PasskeyRevokeResponse = {
  revoked?: number;
  [key: string]: unknown;
};

export type RevokeAllPasskeysResponse = PasskeyRevokeResponse;

function authSurfaceConfig(options?: PasskeyApiRequestOptions) {
  const config: {
    headers: { 'X-AC-Use-Bearer': 'true' };
    _acBearerToken?: string;
  } = {
    headers: {
      'X-AC-Use-Bearer': 'true',
    },
  };
  const bearerToken = options?.bearerToken?.trim();
  if (bearerToken) {
    config._acBearerToken = bearerToken;
  }
  return config;
}

function readStorage(name: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    const storage = globalThis as typeof globalThis &
      Record<typeof name, Storage | undefined>;
    return storage[name] ?? null;
  } catch {
    return null;
  }
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function requireNormalizedEmail(email: string): string {
  const normalized = normalizeEmail(email);
  if (!normalized) {
    throw new Error('Email obrigatorio para passkey.');
  }
  return normalized;
}

function assertExplicitRevokeAllConsent(input: RevokeAllPasskeysWithExplicitConsentInput): void {
  if (
    !input ||
    input.explicitConsent !== true ||
    input.acknowledgedBlastRadius !== PASSKEY_REVOKE_ALL_BLAST_RADIUS
  ) {
    throw new Error(
      'revokeAllPasskeysWithExplicitConsent exige consentimento explicito e reconhecimento do blast radius.',
    );
  }
}

function assertExplicitScopedRevokeConsent(
  input: PasskeyScopedRevokeInput,
  operationName: string,
): void {
  if (!input || input.explicitConsent !== true) {
    throw new Error(`${operationName} exige consentimento explicito.`);
  }
}

function revokePayload(reason?: string) {
  const normalizedReason = reason?.trim();
  return normalizedReason ? { reason: normalizedReason } : {};
}

function scopedRevokePayload(input: PasskeyScopedRevokeInput, defaultReason: string) {
  const payload = revokePayload(revokeReasonOrDefault(input, defaultReason));
  const fcmToken = input.fcmToken?.trim();
  return fcmToken ? { ...payload, fcmToken } : payload;
}

function optionalStringPayload<T extends string>(
  input: Partial<Record<T, string | undefined>> | undefined,
  keys: T[],
): Record<string, string> {
  const payload: Record<string, string> = {};
  if (!input) return payload;

  for (const key of keys) {
    const value = input[key]?.trim();
    if (value) {
      payload[String(key)] = value;
    }
  }
  return payload;
}

function revokeReasonOrDefault(input: PasskeyScopedRevokeInput, defaultReason: string): string {
  return input.reason?.trim() || defaultReason;
}

export function getStoredAuthToken(): string | null {
  const sessionStorageRef = readStorage('sessionStorage');
  const localStorageRef = readStorage('localStorage');

  try {
    if (sessionStorageRef?.getItem('ac_impersonate') === 'true') {
      return sessionStorageRef.getItem('ac_token');
    }
    return localStorageRef?.getItem('ac_token') ?? sessionStorageRef?.getItem('ac_token') ?? null;
  } catch {
    return null;
  }
}

export async function revokeAllPasskeysWithExplicitConsent(
  input: RevokeAllPasskeysWithExplicitConsentInput,
): Promise<RevokeAllPasskeysResponse> {
  assertExplicitRevokeAllConsent(input);

  const { data } = await api.post<RevokeAllPasskeysResponse>(
    '/auth/passkey/revoke-all',
    revokePayload(input.reason),
    authSurfaceConfig(),
  );
  return data;
}

export async function revokeCurrentDevicePasskeyWithExplicitConsent(
  input: PasskeyScopedRevokeInput,
): Promise<PasskeyRevokeResponse> {
  assertExplicitScopedRevokeConsent(input, 'revokeCurrentDevicePasskeyWithExplicitConsent');

  const { data } = await api.post<PasskeyRevokeResponse>(
    '/auth/passkey/revoke-current-device',
    scopedRevokePayload(input, 'pwa-device-disconnect'),
    authSurfaceConfig(),
  );
  return data;
}

export async function revokeMobileBondingPasskeyWithExplicitConsent(
  input: PasskeyScopedRevokeInput,
): Promise<PasskeyRevokeResponse> {
  assertExplicitScopedRevokeConsent(input, 'revokeMobileBondingPasskeyWithExplicitConsent');

  const { data } = await api.post<PasskeyRevokeResponse>(
    '/auth/passkey/revoke-mobile-bonding',
    scopedRevokePayload(input, 'security-bonding-reset'),
    authSurfaceConfig(),
  );
  return data;
}

export async function getPasskeyStatus(
  options?: PasskeyApiRequestOptions,
): Promise<PasskeyStatusResponse> {
  const { data } = await api.get<PasskeyStatusResponse>(
    '/auth/passkey/status',
    authSurfaceConfig(options),
  );
  return data;
}

export async function requestPasskeyRegistrationOptions(
  input?: PasskeyRegistrationOptionsInput,
  options?: PasskeyApiRequestOptions,
): Promise<PasskeyRegistrationOptionsResponse> {
  const payload: Record<string, any> = {
    ...optionalStringPayload(input, ['nomeDispositivo']),
  };
  if (typeof input?.isMobile === 'boolean') {
    payload.isMobile = input.isMobile;
  }
  const { data } = await api.post<PasskeyRegistrationOptionsResponse>(
    '/auth/passkey/register-options',
    payload,
    authSurfaceConfig(options),
  );
  return data;
}

export async function verifyPasskeyRegistration(
  response: PasskeyRegistrationResponse,
  input?: PasskeyRegistrationVerifyOptions,
  options?: PasskeyApiRequestOptions,
): Promise<PasskeyVerifyResponse> {
  const { data } = await api.post<PasskeyVerifyResponse>(
    '/auth/passkey/register-verify',
    {
      response,
      ...optionalStringPayload(input, [
        'nomeDispositivo',
        'navegador',
        'sistemaOp',
        'fingerprint',
      ]),
    },
    authSurfaceConfig(options),
  );
  return data;
}

export async function requestPasskeyAuthOptions(
  options?: PasskeyApiRequestOptions,
): Promise<PasskeyAuthenticationOptionsResponse> {
  const { data } = await api.post<PasskeyAuthenticationOptionsResponse>(
    '/auth/passkey/auth-options',
    {},
    authSurfaceConfig(options),
  );
  return data;
}

export async function verifyPasskeyAuthentication(
  response: PasskeyAuthenticationResponse,
  options?: PasskeyApiRequestOptions,
): Promise<PasskeyVerifyResponse> {
  const { data } = await api.post<PasskeyVerifyResponse>(
    '/auth/passkey/auth-verify',
    { response },
    authSurfaceConfig(options),
  );
  return data;
}

export async function requestLoginPasskeyOptions(
  email: string,
): Promise<PasskeyAuthenticationOptionsResponse> {
  const { data } = await api.post<PasskeyAuthenticationOptionsResponse>(
    '/auth/login-passkey-options',
    { email: requireNormalizedEmail(email) },
  );
  return data;
}

export async function getPublicPasskeyConfig(): Promise<PublicPasskeyConfig> {
  const { data } = await api.get<PublicPasskeyConfig>('/auth/passkey-config');
  return data;
}

export async function verifyLoginPasskey(
  email: string,
  response: PasskeyAuthenticationResponse,
): Promise<LoginPasskeyVerifyResponse> {
  const { data } = await api.post<LoginPasskeyVerifyResponse>(
    '/auth/login-passkey-verify',
    { email: requireNormalizedEmail(email), response },
  );
  return data;
}
